import { create } from "zustand";
import type { RecurringExpenseSuggestionItem } from "@boo/shared";
import {
  fetchRecurringExpenseSuggestions,
  triggerSuggestionDetection,
  confirmSuggestion as apiConfirm,
  dismissSuggestion as apiDismiss,
  editAndConfirmSuggestion as apiEditAndConfirm,
  type EditConfirmSuggestionPayload,
} from "../../../services/recurringExpenseApi";

interface RecurringExpenseSuggestionState {
  suggestions: RecurringExpenseSuggestionItem[];
  isLoading: boolean;
  loadSuggestions: () => Promise<void>;
  triggerDetection: () => Promise<void>;
  confirmSuggestion: (id: string) => Promise<void>;
  dismissSuggestion: (id: string) => Promise<void>;
  editAndConfirmSuggestion: (
    id: string,
    overrides: EditConfirmSuggestionPayload,
  ) => Promise<void>;
}

export const useRecurringExpenseSuggestionStore =
  create<RecurringExpenseSuggestionState>()((set, get) => ({
    suggestions: [],
    isLoading: false,

    async loadSuggestions() {
      set({ isLoading: true });
      try {
        const data = await fetchRecurringExpenseSuggestions();
        set({ suggestions: data });
      } catch {
        // non-critical — section will show empty state
      } finally {
        set({ isLoading: false });
      }
    },

    async triggerDetection() {
      set({ isLoading: true });
      try {
        const data = await triggerSuggestionDetection();
        set({ suggestions: data });
      } finally {
        set({ isLoading: false });
      }
    },

    async confirmSuggestion(id: string) {
      const prev = get().suggestions;
      set({ suggestions: prev.filter((s) => s.id !== id) });
      try {
        await apiConfirm(id);
      } catch {
        set({ suggestions: prev });
      }
    },

    async dismissSuggestion(id: string) {
      const prev = get().suggestions;
      set({ suggestions: prev.filter((s) => s.id !== id) });
      try {
        await apiDismiss(id);
      } catch {
        set({ suggestions: prev });
      }
    },

    async editAndConfirmSuggestion(
      id: string,
      overrides: EditConfirmSuggestionPayload,
    ) {
      const prev = get().suggestions;
      set({ suggestions: prev.filter((s) => s.id !== id) });
      try {
        await apiEditAndConfirm(id, overrides);
      } catch {
        set({ suggestions: prev });
      }
    },
  }));
